import { Box, Button, Typography, useTheme } from "@mui/material"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import CustomSearchBar from "../../components/common/CustomSearchBar"
import { observer } from "mobx-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAdmissionExamStore } from "../../store/admission-exam.store"
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import TryAgainMessageBlock from "../../components/common/TryAgainMessageBlock"
import noExamImg from '../../assets/noexams.svg'
import { Icons, formatDate, time24to12 } from '../../utils/utilities'
import ExamInstructionsDialog from "../../components/admission-exam/ExamInstructionsDialog"
import { useUserStore } from "../../store/user.store"
import warningImg from '../../assets/warning.svg'
import CustomTabs from '../../components/common/CustomTabs'
import CustomTable from '../../components/common/custom-table/CustomTable'


const tabs = [
  { label: "Published", value: "Published", icon: Icons.default.CheckboxChecked },
  { label: "Draft", value: "Draft", icon: Icons.default.Checkbox },
]

const AdmissionExams = observer( ( props ) => {
  const [loading, setLoading] = useState( true )
  const [error, setError] = useState( null )
  const [tabValue, setTabValue] = useState( "Published" )
  const [hasPermission, setHasPermission] = useState( true )
  const [publishedExams, setpublishedExams] = useState( [] )
  const [activeExams, setActiveExams] = useState( [] )
  const { palette, layout } = useTheme()

  const [searchParams, setSearchParams] = useSearchParams( { searchText: "" } )
  const searchText = searchParams.get( 'searchText' )
  const [instructionsDialogState, setInstructionsDialogState] = useState( false )

  const navigate = useNavigate()

  const columns = useMemo( () => {
    return [
      { id: 0, index: true, field: "index", name: "Sl.no", padding: "10px 20px" },
      { id: 1, field: "exam_name", width: '150px', maxWidth: "200px", name: "Exam name" },
      {
        id: 2, renderCell: ( row ) => {
          return <Box>
            <Typography component="span" sx={{ fontSize: "12px", textTransform: "uppercase" }}>{row.global_exam_access_courses?.slice( 0, 2 )?.join( ", " )} </Typography>
            {row.global_exam_access_courses?.length > 2 && <Typography component="span" sx={{ fontSize: "12px" }}><em>{` and ${row.global_exam_access_courses?.length - 2} more`} </em></Typography>}
          </Box>
        }, name: "For Course(s)", minWidth: "130px"
      },
      { id: 3, width: "120px", renderCell: row => formatDate( row?.exam_start_date, { format: "DD-MM-YYYY" } ) + ", " + time24to12( row?.exam_start_time ), name: "Starts At", minWidth: "130px", padding: "10px 20px" },
      { id: 6, width: "150px", renderCell: row => row?.exam_constraints?.academicYear || "NA", name: "Academic Year", align: "center", minWidth: "130px", padding: "10px 20px" },
      {
        id: 5, renderCell: row => {
          return (
            <Box display="flex" alignItems="center" gap="10px">
              {row.exam_status}
              {activeExams.includes( row.exam_id ) && <Typography borderRadius="5px" fontSize="10px" padding="2px 5px" bgcolor={palette.success.main} color="white">Active</Typography>}
              {row.closed && <Typography borderRadius="5px" fontSize="10px" padding="2px 5px" bgcolor={palette.errorMessage.main} color="white">Closed</Typography>}
            </Box>
          )
        }, name: "Status"
      },
      { id: 4, field: "participants", hide: tabValue !== "Published", name: "Participants", align: "center", padding: "10px 20px" },
    ]
  }, [tabValue, activeExams, palette] )

  const AdmissionExamStore = useAdmissionExamStore()
  const UserStore = useUserStore()

  const handleSearch = e => {
    setSearchParams( prev => {
      prev.set( 'searchText', e.target.value )
      return prev
    }, { replace: true } )
  }

  const getExams = useCallback( async () => {
    setLoading( true )
    const { success, error } = await AdmissionExamStore.fetchExams( { exam_type: "global" } )
    if ( success ) {
      setpublishedExams( AdmissionExamStore.getExams.filter( exam => exam.exam_status === "Published" && !exam.closed ) )
      setError( null )
    } else {
      console.log( error )
      setError( error )
    }
    setLoading( false )
  }, [AdmissionExamStore] )

  const processedRows = AdmissionExamStore.getExams.filter( exam => exam.exam_status === tabValue && exam.exam_name?.match( new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' ) ) )

  useEffect( () => {
    if ( UserStore.getUser.user_role === 'SUBADMIN' && !UserStore.getUser.user_permissions.admission_exams ) {
      setHasPermission( false )
    }
  }, [UserStore] )

  useEffect( () => {
    getExams()
  }, [getExams] )

  useEffect( () => {
    let activeExams = []
    let activeMap = {}
    if ( publishedExams?.length > 0 && publishedExams[0].global_exam_access_courses?.includes( 'all' ) ) {
      activeMap['all'] = publishedExams[0].exam_id
      activeExams.push( publishedExams[0].exam_id )
    } else {
      for ( let i = 0; i < publishedExams.length; i++ ) {
        const examBranches = publishedExams[i].global_exam_access_courses
        for ( let k = 0; k < examBranches.length; k++ ) {
          const branch = examBranches[k]
          if ( !activeMap[branch] ) {
            activeMap[branch] = true
            activeExams.push( publishedExams[i].exam_id )
          }
        }
      }
      setActiveExams( activeExams )
    }
  }, [publishedExams] )

  return (
    <Box flexGrow={1} overflow="auto" padding={layout.pagePadding} display="flex" flexDirection="column">
      <ExamInstructionsDialog state={instructionsDialogState} setState={setInstructionsDialogState} instructionsDetails={{}} />
      {hasPermission ? <Box overflow="auto" display="flex" flexDirection="column" gap={layout.sectionsGap} flexGrow={1}>
        <Box>
          <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Assessments</Typography>
          <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Manage all assessments here..</Typography>
        </Box>

        <Box display="flex" gap={layout.cardsGap}>
          <CustomSearchBar style={{ flexGrow: 1 }} onChange={handleSearch} value={searchText} />
          <Button onClick={() => navigate( `/admin/assessment/new` )} sx={{ textTransform: "capitalize", paddingX: "20px", backgroundColor: "#BEFFC7", color: "black", "&:hover": { backgroundColor: "#7ffa90" } }} >
            Create Assessment
          </Button>
          <Button variant="contained" sx={{ textTransform: "capitalize" }} disableElevation onClick={() => setInstructionsDialogState( true )} color="warning">
            Set assessment instructions
          </Button>
        </Box>
        {/* {!loading && AdmissionExamStore.getExams?.length !== publishedExams?.length && tabValue === 'Published' && <Box margin="20px" padding="10px" borderRadius="5px" border={`1px solid ${palette.errorMessage.main}`} bgcolor={palette.error.light + "22"} >
          <Typography variant="subtitle2" color="errorMessage.main">Some assessments are due for approval!</Typography>
          <Button sx={{ textTransform: "capitalize", marginTop: "10px" }} onClick={() => setTabValue( "Draft" )} color="errorMessage" variant="contained" disableElevation >View Draft Assessments</Button>
        </Box>} */}

        {!loading && !error && <Box overflow="auto" flexGrow={1} display="flex" gap="10px" flexDirection="column">
          <CustomTabs value={tabValue} tabs={tabs} handleTabChange={setTabValue} />
          {processedRows.length > 0 && <CustomTable columns={columns} onRowClick={row => navigate( `/admin/assessment/${row.exam_id}` )} data={processedRows} />}
          {processedRows.length === 0 && <Box flexGrow={1} borderRadius="5px" flexDirection="column" display="flex" justifyContent="center" alignItems="center" padding="20px" bgcolor={palette.contentBg} gap="20px" color="textSecondary" variant='subtitle2'>
            <Box component="img" src={noExamImg} width="200px" alt="no exams!" />
            <Typography variant='subtitle2'>{tabValue === "Draft" ? "No exam needs approval!" : `No published exams`} {searchText?.trim() && tabValue !== 'Draft' ? <span>for <em>{searchText}</em> </span> : ""}</Typography>
            {AdmissionExamStore.getExams.length === 0 && <Button sx={{ textTransform: "capitalize" }} variant='outlined' startIcon={Icons.default.AddIcon} onClick={() => navigate( `/admin/assessment/new` )} >Create One</Button>}
          </Box>}
        </Box>}

        {loading && !error && <DataLoadingSpinner waitingMessage="Loading exams please wait" />}
        {error && <TryAgainMessageBlock code={error.code} err={error.message} getData={getExams} loading={loading} />}
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", padding: "20px", gap: "20px", alignItems: "center" }}>
        <img src={warningImg} width="40px" alt="No permission" />
        <Typography variant='subtitle2' color="textSecondary">You doesn't have permission to access this page!</Typography>
      </Box>}
    </Box>
  )
} )

export default AdmissionExams
